import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import fraction from "src/api/fraction";
import { handleToken, STORAGE } from "src/auth/mutations";
import useQueryParams from "src/hooks/useQueryParams";
import { useTokenStore } from "src/stores/tokenStore";
import useEffectOnce from "./useEffectOnce";

// Keep in sync with the user query key in src/auth/useAuth.tsx.
const AUTH_QUERY_KEY = ["authenticate"];

function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  if (!base64Url) {
    return null;
  }
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join("")
  );

  return JSON.parse(jsonPayload);
}

/**
 * Read/write the auth token. Deliberately lightweight: it only touches the
 * zustand token store (shared across all instances) and decodes the JWT — it
 * does NOT subscribe to the router. The URL magic-link capture lives in
 * `useCaptureUrlToken`, mounted once at the app root, so the ~60 components that
 * read the token don't each re-render on every URL change.
 */
const useToken = () => {
  // Shared token state via zustand — all useToken instances stay in sync
  const token = useTokenStore((state) => state.token);
  const setTokenInStore = useTokenStore((state) => state.setToken);

  const json = useMemo(() => (token ? parseJwt(token) : undefined), [token]);

  const setToken = useCallback(
    (t?: string | null) => {
      const value = t || null;
      setTokenInStore(value);
      if (value) {
        handleToken(value);
      }
    },
    [setTokenInStore]
  );

  const clearToken = useCallback(() => {
    setToken(null);
  }, [setToken]);

  return {
    isLoading: false,
    token: token || undefined,
    isSet: !!token,
    setToken,
    clearToken,
    id: json?.id,
    email: json?.email,
  };
};

/**
 * Captures a `?token=` magic-link param into the token store and strips it from
 * the URL. The initial page load is already handled synchronously in the token
 * store; this covers in-app navigation to a magic link. Mount this ONCE near the
 * app root — it subscribes to the router, which is why it's kept out of
 * `useToken` itself.
 */
export const useCaptureUrlToken = () => {
  const params = useQueryParams();
  const [, setSearchParams] = useSearchParams();
  const { setToken } = useToken();
  const queryClient = useQueryClient();

  // Stable reference — uses functional updater so no dependency on searchParams
  const removeParams = useCallback(
    (...keys: string[]) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        for (const key of keys) {
          newParams.delete(key);
        }
        return newParams;
      });
    },
    [setSearchParams]
  );

  const paramToken = params.get("token");
  // `verifyEmail=1` rides on the registration magic link: opening it should
  // actually verify the email (graduate the account), not just authenticate.
  const shouldVerifyEmail = params.get("verifyEmail") === "1";

  useEffectOnce(
    () => {
      if (paramToken && paramToken !== STORAGE.getToken()) {
        setToken(paramToken);
      }
      if (paramToken && shouldVerifyEmail) {
        fraction
          .verifyEmail(paramToken)
          .catch(() => {
            // Best-effort: an expired/invalid link just leaves them unverified;
            // they can still verify via the OTP code on the verify screen.
          })
          .finally(() => {
            queryClient.refetchQueries({ queryKey: AUTH_QUERY_KEY });
          });
      }
      removeParams("token", "verifyEmail");
    },
    [paramToken, shouldVerifyEmail, removeParams, setToken, queryClient],
    !paramToken?.length
  );
};

export default useToken;
